import React, { useEffect, useRef, useState } from "react";
import { FiSend, FiArrowLeft, FiChevronLeft, FiEdit2, FiTrash } from "react-icons/fi";
import socket from "../../../../socket";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatWindow = ({ chatId, onMessagesSeen, onBack, chatPartnerInfo }) => {
  const currentUserEmail = localStorage.getItem("userEmail");
  const token = localStorage.getItem("token");

  const [messages, setMessages] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [typingUser, setTypingUser] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [showChatListMobile, setShowChatListMobile] = useState(false);

  const bottomRef = useRef();
  const chatContainerRef = useRef();

  const hasAutoScrolled = useRef(false);

  useEffect(() => {
    const fetchMessages = async () => {
      const res = await fetch(`${BACKEND_URL}/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const normalized = data.messages.map((m) => ({
        ...m,
        timestamp: normalizeTimestamp(m.timestamp),
      }));
      setMessages(normalized);
      await fetchNamesFromMessages(normalized);
    };

    const markAsRead = async () => {
      await fetch(`${BACKEND_URL}/chats/${chatId}/mark-as-read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      socket.emit("seen_message", { chatId, userEmail: currentUserEmail });
      onMessagesSeen?.(chatId);
    };

    fetchMessages();
    markAsRead();
    const interval = setInterval(fetchMessages, 3000);

    socket.emit("join_room", { chatId, userEmail: currentUserEmail });

    socket.on("new_message", async (msg) => {
      if (msg.chatId === chatId) {
        msg.timestamp = normalizeTimestamp(msg.timestamp);
        await fetchNamesFromMessages([msg]);
        setMessages((prev) => [...prev, msg]);
        socket.emit("seen_message", { chatId, userEmail: currentUserEmail });
      }
    });

    socket.on("message_updated", ({ messageId, newMessage }) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, message: newMessage, edited: true } : m))
      );
    });

    socket.on("message_deleted", ({ messageId }) => {
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    });

    socket.on("user_typing", ({ userEmail }) => {
      if (userEmail !== currentUserEmail) {
        setTypingUser(userEmail);
        clearTimeout(window._typingTimeout);
        window._typingTimeout = setTimeout(() => setTypingUser(null), 1500);
      }
    });

    socket.on("message_seen", ({ userEmail }) => {
      if (userEmail !== currentUserEmail) {
        setMessages((prev) =>
          prev.map((m) =>
            m.sender === currentUserEmail
              ? { ...m, readBy: [...(m.readBy || []), userEmail] }
              : m
          )
        );
      }
    });

    return () => {
      clearInterval(interval);
      socket.off("new_message");
      socket.off("message_updated");
      socket.off("message_deleted");
      socket.off("user_typing");
      socket.off("message_seen");
    };
  }, [chatId]);

  useEffect(() => {
  const container = chatContainerRef.current;
  if (!container || messages.length === 0) return;

  const isNearBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight < 150;

  if (isNearBottom) {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }
}, [messages]);


  const fetchNamesFromMessages = async (msgs) => {
    const missing = [...new Set(msgs.map((m) => m.sender))].filter((e) => !nameMap[e]);
    const updated = { ...nameMap };
    await Promise.all(
      missing.map(async (email) => {
        const res = await fetch(`${BACKEND_URL}/user/${encodeURIComponent(email)}`);
        const user = await res.json();
        updated[email] = `${user.firstName || ""} ${user.lastName || ""}`.trim() || email;
      })
    );
    setNameMap(updated);
  };

  
  const formatTime = (timestamp) => {
    if (!timestamp) return "—";
    let date;
    if (timestamp.toDate) date = timestamp.toDate();
    else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
    else date = new Date(timestamp);
    return isNaN(date.getTime())
      ? "—"
      : date.toLocaleString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).replace(",", " •");
  };

  const normalizeTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts === "string") return new Date(ts);
    if (ts.toDate) return ts.toDate();
    if (ts._seconds) return new Date(ts._seconds * 1000);
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return new Date(ts);
  };

  const sendMessage = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed) return;

    if (editingMessageId) {
      await fetch(`${BACKEND_URL}/chats/${chatId}/edit/${editingMessageId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newMessage: trimmed }),
      });
      socket.emit("edit_message", {
        chatId,
        messageId: editingMessageId,
        newMessage: trimmed,
      });
      setEditingMessageId(null);
    } else {
      await fetch(`${BACKEND_URL}/chats/${chatId}/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sender: currentUserEmail, message: trimmed }),
      });
    }

    setNewMessage("");
  };

  const deleteMessage = async (messageId) => {
    await fetch(`${BACKEND_URL}/chats/${chatId}/delete/${messageId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    socket.emit("delete_message", { chatId, messageId });
  };

  return (
  <div className="flex flex-col flex-grow h-full bg-white min-h-0">


    {/* 🔹 Header */}
    <div className="shrink-0 px-3 py-2 border-b bg-white z-10">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img
            src={chatPartnerInfo?.profileImage || "/person.png"}
            className="w-8 h-8 rounded-full object-cover"
            alt="Profile"
          />
          <span className="text-base font-semibold text-gray-800">
            {chatPartnerInfo?.name || "Unknown"}
          </span>
        </div>
        <div className="text-sm text-gray-500">
          {chatId ? `${messages.length} messages` : "—"}
        </div>
      </div>
    </div>

    {/* 🔸 Scrollable Messages */}
    <div className="flex-grow overflow-y-auto px-4 py-2 space-y-4 min-h-0" ref={chatContainerRef}>

      {chatId && messages.length > 0 ? (
        messages.map((msg) => {
          const isMe = msg.sender === currentUserEmail;
          return (
            <div
              key={msg.id}
              className={`max-w-[70%] px-4 py-2 rounded-xl shadow-sm text-sm relative ${
                isMe
                  ? "ml-auto bg-blue-100 text-right text-gray-800"
                  : "mr-auto bg-gray-100 text-left text-gray-700"
              }`}
            >
              <div className="font-semibold text-xs mb-1">
                {isMe ? "You" : nameMap[msg.sender] || msg.sender}
              </div>
              <div className="whitespace-pre-wrap break-words">
                {msg.message}
              </div>

              {isMe && (
                <div className="absolute bottom-1 left-2 flex gap-1">
                  <button
                    onClick={() => {
                      setNewMessage(msg.message);
                      setEditingMessageId(msg.id);
                    }}
                      className="text-gray-500 hover:text-blue-600"
                  >
                    <FiEdit2 size={14} />
                  </button>
                  <button
                    onClick={() => deleteMessage(msg.id)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <FiTrash size={14} />
                  </button>
                </div>
              )}

              <div className="text-[10px] mt-1 text-gray-500">
                {formatTime(msg.timestamp)} {msg.edited && "(edited)"}{" "}
                {isMe &&
                  (msg.readBy?.length > 1
                    ? "✓ Seen"
                    : msg.readBy?.includes(currentUserEmail)
                    ? "✓ Delivered"
                    : "✓ Sent")}
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center text-sm text-gray-400 mt-6">
          {chatId
            ? "No messages yet. Start the conversation."
            : "Select a conversation to start chatting."}
        </div>
      )}

      {typingUser && (
        <div className="text-xs italic text-gray-400">
          {nameMap[typingUser] || typingUser} is typing...
        </div>
      )}

      <div ref={bottomRef} className="h-0" />
    </div>

    {/* 🔻 Fixed Input */}
    <div className="shrink-0 px-4 py-2 border-t bg-white z-10 flex items-center">
      <input
        type="text"
        className="flex-grow text-sm border px-3 py-1 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        placeholder="Type a message..."
        value={newMessage}
        onChange={(e) => {
          setNewMessage(e.target.value);
          socket.emit("typing", { chatId, userEmail: currentUserEmail });
        }}
        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        style={{ height: "32px" }}
      />
      <button
        onClick={sendMessage}
        className="ml-2 p-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300"
        disabled={!chatId}
        style={{ height: "36px", minWidth: "36px" }}
      >
        <FiSend size={18} />
      </button>
    </div>
  </div>
);

};

export default ChatWindow;
